/**
 * Tenant Context Store
 * Manages the currently selected tenant for settings configuration
 * Implements session locking to prevent accidental cross-tenant updates
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { tenantService } from '@services/tenant.service';

export interface TenantContextState {
  // Current tenant being managed
  currentTenant: {
    id: string;
    name: string;
    slug: string;
    primaryColor?: string;
    logoUrl?: string;
    plan?: string;
    status?: string;
  } | null;

  // Session lock - prevents accidental tenant switches
  isLocked: boolean;
  lockedAt: string | null;

  // Loading state
  isLoading: boolean;
  error: string | null;

  // Actions
  setTenant: (tenant: TenantContextState['currentTenant']) => void;
  lockSession: () => void;
  unlockSession: () => void;
  clearTenant: () => void;
  loadTenant: (tenantId: string) => Promise<void>;

  // Hydration state
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
}

export const useTenantContextStore = create<TenantContextState>()(
  persist(
    (set, get) => ({
      currentTenant: null,
      isLocked: false,
      lockedAt: null,
      isLoading: false,
      error: null,
      _hasHydrated: false,

      setTenant: (tenant) => {
        set({
          currentTenant: tenant,
          isLocked: true,
          lockedAt: new Date().toISOString(),
          error: null,
        });
      },

      lockSession: () => {
        set({
          isLocked: true,
          lockedAt: new Date().toISOString(),
        });
      },

      unlockSession: () => {
        set({
          isLocked: false,
          lockedAt: null,
        });
      },

      clearTenant: () => {
        set({
          currentTenant: null,
          isLocked: false,
          lockedAt: null,
          error: null,
        });
      },

      loadTenant: async (tenantId: string) => {
        set({ isLoading: true, error: null });
        try {
          // Fetch tenant and subscription in parallel
          const [tenant, subscription] = await Promise.all([
            tenantService.getTenant(tenantId),
            tenantService.getTenantSubscription(tenantId).catch(() => null),
          ]);

          set({
            currentTenant: {
              id: tenant.id,
              name: tenant.name,
              slug: tenant.slug,
              plan: subscription?.plan,
              status: tenant.status,
            },
            isLocked: true,
            lockedAt: new Date().toISOString(),
            isLoading: false,
          });
        } catch (error: any) {
          set({
            error: error.message || 'Failed to load tenant',
            isLoading: false,
          });
          throw error;
        }
      },

      setHasHydrated: (state) => {
        set({ _hasHydrated: state });
      },
    }),
    {
      name: 'tenant-context-storage',
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
      partialize: (state) => ({
        currentTenant: state.currentTenant,
        isLocked: state.isLocked,
        lockedAt: state.lockedAt,
      }),
    }
  )
);

// Helper hook to check if user can switch tenants
export const useCanSwitchTenant = () => {
  const { isLocked, currentTenant } = useTenantContextStore();
  return !isLocked || !currentTenant;
};

// Helper hook to get tenant accent color
export const useTenantAccentColor = () => {
  const { currentTenant } = useTenantContextStore();
  return currentTenant?.primaryColor || '#3B82F6'; // Default blue
};
